import React from 'react';

export interface ProgressBarProps {
  value: number;
  max?: number;
  showLabel?: boolean;
  label?: string;
  variant?: 'primary' | 'success' | 'warning' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  animate?: boolean;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  showLabel = false,
  label,
  variant = 'primary',
  size = 'md',
  className = '',
  animate = true
}) => {
  // Ensure value is between 0 and max
  const normalizedValue = Math.max(0, Math.min(value, max));
  const percentage = Math.round((normalizedValue / max) * 100);

  const getVariantClasses = () => {
    switch (variant) {
      case 'success':
        return 'bg-green-600';
      case 'warning':
        return 'bg-yellow-600';
      case 'danger':
        return 'bg-red-600';
      case 'primary':
      default:
        return 'bg-primary-600';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'h-1';
      case 'md':
        return 'h-2';
      case 'lg':
        return 'h-3';
    }
  };

  const progressFillClasses = `${getSizeClasses()} ${getVariantClasses()} rounded-full ${
    animate ? 'transition-all duration-300' : ''
  }`;

  return (
    <div className={className}>
      {(showLabel || label) && (
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          {label && <span>{label}</span>}
          <span>{percentage}%</span>
        </div>
      )}
      <div className={`w-full bg-gray-200 rounded-full ${getSizeClasses()}`}>
        <div
          className={progressFillClasses}
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={normalizedValue}
          aria-valuemin={0}
          aria-valuemax={max}
        />
      </div>
    </div>
  );
};

export default ProgressBar;